import { get, post, put, del } from '../utils/request';
import { apiCache, createCacheKey } from '../utils/apiCache';

// Case status - 已改为动态枚举，通过 CASE_STATUS 分类从后台配置获取
// 以下 enum 仅作兼容保留，新代码请使用 string 类型
/** @deprecated 使用动态枚举，case_status 字段类型为 string */
export enum CaseStatus {
  PREPARING_FILING = 'preparing_filing',
  FILED = 'filed',
  SUPPLEMENTING = 'supplementing',
  MEDIATION = 'mediation',
  HEARING = 'hearing',
  AWAITING_VERDICT = 'awaiting_verdict',
  FIRST_INSTANCE = 'first_instance',
  SECOND_INSTANCE = 'second_instance',
  RETRIAL = 'retrial',
  ENFORCEMENT = 'enforcement',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  MEDIATION_CLOSED = 'mediation_closed',
  VERDICT_CLOSED = 'verdict_closed',
  CLOSED = 'closed',
}

// Party type enum
export enum PartyType {
  OUR_SIDE = 'our_side',
  OTHER_SIDE = 'other_side',
}

// Party role enum
export enum PartyRole {
  PLAINTIFF = 'plaintiff',
  DEFENDANT = 'defendant',
  CLIENT = 'client',
  THIRD_PARTY = 'third_party',
  ENFORCEMENT_APPLICANT = 'enforcement_applicant',
  ENFORCEMENT_RESPONDENT = 'enforcement_respondent',
  SUSPECT_FAMILY = 'suspect_family',
  SUSPECT = 'suspect',
}

// Case type enum
export enum CaseType {
  CIVIL = 'civil',
  CRIMINAL = 'criminal',
  CORPORATE = 'corporate',
  FAMILY = 'family',
  LABOR = 'labor',
  PROPERTY = 'property',
  INTELLECTUAL_PROPERTY = 'intellectual_property',
  ADMINISTRATIVE = 'administrative',
  OTHER = 'other',
}

// Case priority enum
export enum CasePriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

// Case party interface
export interface CaseParty {
  id?: number;
  case_id?: number;
  party_type: PartyType;
  party_role: PartyRole;
  name: string;
  gender?: string;
  identity_type?: string;
  identity_number?: string;
  phone?: string;
  address?: string;
  legal_representative?: string;
  created_at?: string;
  updated_at?: string;
}

// Case detail interface
export interface CaseDetail {
  id?: number;
  case_id?: number;
  case_reason?: string;
  case_level?: string;
  court_name?: string;
  court_contact?: string;
  case_summary?: string;
  fee_situation?: string;
  secondary_reason?: string;
  notes?: string;
  custom_fields?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

// Case milestone interface
export interface CaseMilestone {
  id: number;
  case_id: number;
  milestone_type: string;
  description: string;
  operator_id: number;
  operator_name?: string;
  created_at: string;
}

// Case interface
export interface Case {
  id: number;
  case_number: string;
  case_name: string;
  case_type: string;
  case_stage?: string;
  case_status: string;
  accept_date?: string;
  court_date?: string;
  verdict_date?: string;
  next_follow_up_time?: string;  // 下次跟进时间
  lead_id?: number;
  responsible_lawyer_id: number;
  responsible_lawyer_name?: string;
  assistant_lawyer_id?: number;
  assistant_lawyer_name?: string;
  created_by: number;
  created_by_name?: string;
  wechat_group_id?: number;
  wechat_group?: {
    id: number;
    group_id: string;
    group_name: string;
  };
  parties?: CaseParty[];
  details?: CaseDetail;
  milestones?: CaseMilestone[];
  permissions?: {
    can_view: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_invite: boolean;
    can_manage_finance: boolean;
  };
  created_at: string;
  updated_at: string;
  
  // Legacy fields for backward compatibility
  title?: string;
  description?: string;
  type?: CaseType;
  status?: string;
  priority?: CasePriority;
  clientId?: number;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  assignedLawyer?: number;
  assignedLawyerName?: string;
  estimatedValue?: number;
  actualValue?: number;
  startDate?: string;
  expectedEndDate?: string;
  actualEndDate?: string;
  courtName?: string;
  caseNumber?: string;
  tags?: string[];
  documents?: CaseDocument[];
}

// Case document interface
export interface CaseDocument {
  id: number;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedBy: number;
  uploadedByName: string;
  uploadedAt: string;
}

// Case timeline interface
export interface CaseTimeline {
  id: number;
  caseId: number;
  type: 'note' | 'document' | 'hearing' | 'meeting' | 'payment' | 'status_change';
  title: string;
  content: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
}

// Case list request interface
export interface CaseListRequest {
  page?: number;
  pageSize?: number;
  status?: string;
  type?: CaseType;
  priority?: CasePriority;
  assignedLawyer?: number;
  clientId?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  tags?: string[];
}

// Case list response interface
export interface CaseListResponse {
  items: Case[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// Create case request interface
export interface CreateCaseRequest {
  title: string;
  description?: string;
  type: CaseType;
  priority: CasePriority;
  clientId?: number;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  assignedLawyer?: number;
  estimatedValue?: number;
  expectedEndDate?: string;
  courtName?: string;
  caseNumber?: string;
  tags?: string[];
}

// Update case request interface
export interface UpdateCaseRequest extends Partial<CreateCaseRequest> {
  status?: string;
  actualValue?: number;
  actualEndDate?: string;
  lead_id?: number; // 关联线索ID
}

// Case statistics interface
export interface CaseStatistics {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  closed: number;
  cancelled: number;
  totalValue: number;
  averageValue: number;
  completionRate: number;
}

/**
 * Get case list (with caching)
 */
export const getCaseList = async (params: CaseListRequest): Promise<CaseListResponse> => {
  const cacheKey = createCacheKey('case-list', params);
  return apiCache.wrap(
    cacheKey,
    () => get<CaseListResponse>('/cases', { params }),
    { ttl: 2 * 60 * 1000 } // 2 minutes cache for list
  );
};

/**
 * Get case by ID (with caching)
 */
export const getCaseById = async (id: number): Promise<Case> => {
  const cacheKey = `case-detail:${id}`;
  
  // 清除缓存，强制重新加载
  apiCache.delete(cacheKey);
  
  // 添加时间戳防止浏览器缓存
  return get<Case>(`/cases/${id}?_t=${Date.now()}`);
};

/**
 * Create new case
 */
export const createCase = async (data: CreateCaseRequest): Promise<Case> => {
  const result = await post<Case>('/cases', data);
  // Invalidate list cache
  apiCache.clearPattern(/^case-list:/);
  return result;
};

/**
 * Update case
 */
export const updateCase = async (id: number, data: UpdateCaseRequest): Promise<Case> => {
  const result = await put<Case>(`/cases/${id}`, data);
  // Invalidate specific case and list cache
  apiCache.delete(`case-detail:${id}`);
  apiCache.clearPattern(/^case-list:/);
  return result;
};

/**
 * Delete case
 */
export const deleteCase = async (id: number): Promise<void> => {
  await del<void>(`/cases/${id}`);
  // Invalidate specific case and list cache
  apiCache.delete(`case-detail:${id}`);
  apiCache.clearPattern(/^case-list:/);
};

/**
 * 检查企业微信群是否可用于案件关联
 */
export const checkWechatGroupAvailability = async (
  wechatGroupId: number,
  excludeCaseId?: number
): Promise<{ available: boolean; message?: string }> => {
  try {
    const params: Record<string, number | undefined> = { wechat_group_id: wechatGroupId };
    if (excludeCaseId) {
      params.exclude_case_id = excludeCaseId;
    }
    // 正确传递参数：第二个参数应该是AxiosRequestConfig对象
    const response = await get<{ available: boolean; message?: string }>('/cases/check-wechat-group', { params });
    return response;
  } catch (error: unknown) {
    // 如果是409错误，返回不可用状态
    if (error && typeof error === 'object' && 'response' in error) {
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      if (err.response?.status === 409) {
        return {
          available: false,
          message: err.response.data?.detail,
        };
      }
    }
    throw error;
  }
};

/**
 * Assign case to lawyer
 */
export const assignCase = async (id: number, assignedLawyer: number): Promise<Case> => {
  const result = await put<Case>(`/cases/${id}/assign`, { assignedLawyer });
  // Invalidate cache
  apiCache.delete(`case-detail:${id}`);
  apiCache.clearPattern(/^case-list:/);
  return result;
};

/**
 * Update case status
 */
export const updateCaseStatus = async (id: number, status: string): Promise<Case> => {
  const result = await put<Case>(`/cases/${id}/status`, { status });
  // Invalidate cache
  apiCache.delete(`case-detail:${id}`);
  apiCache.clearPattern(/^case-list:/);
  return result;
};

/**
 * Get case timeline
 */
export const getCaseTimeline = async (caseId: number): Promise<CaseTimeline[]> => {
  return get<CaseTimeline[]>(`/cases/${caseId}/timeline`);
};

/**
 * Add case timeline entry
 */
export const addCaseTimeline = async (caseId: number, data: {
  type: string;
  title: string;
  content: string;
}): Promise<CaseTimeline> => {
  return post<CaseTimeline>(`/cases/${caseId}/timeline`, data);
};

/**
 * Upload case document
 */
export const uploadCaseDocument = async (caseId: number, file: File): Promise<CaseDocument> => {
  const formData = new FormData();
  formData.append('file', file);

  return post<CaseDocument>(`/cases/${caseId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Delete case document
 */
export const deleteCaseDocument = async (caseId: number, documentId: number): Promise<void> => {
  return del<void>(`/cases/${caseId}/documents/${documentId}`);
};

/**
 * Get case statistics (with caching)
 */
export const getCaseStatistics = async (params?: {
  startDate?: string;
  endDate?: string;
  assignedLawyer?: number;
  type?: CaseType;
}): Promise<CaseStatistics> => {
  const cacheKey = createCacheKey('case-statistics', params);
  return apiCache.wrap(
    cacheKey,
    () => get<CaseStatistics>('/cases/statistics', { params }),
    { ttl: 3 * 60 * 1000 } // 3 minutes cache for statistics
  );
};

/**
 * Export cases to Excel
 */
export const exportCases = async (params: CaseListRequest): Promise<Blob> => {
  return get<Blob>('/cases/export', {
    params,
    responseType: 'blob',
  });
};

/**
 * Get available case tags
 */
export const getCaseTags = async (): Promise<string[]> => {
  return get<string[]>('/cases/tags');
};

/**
 * Search cases by text
 */
export const searchCases = async (query: string, params?: {
  type?: CaseType;
  status?: string;
  assignedLawyer?: number;
}): Promise<Case[]> => {
  return get<Case[]>('/cases/search', {
    params: {
      q: query,
      ...params,
    },
  });
};

/**
 * Get my assigned cases
 */
export const getMyCases = async (params?: {
  status?: string;
  priority?: CasePriority;
  page?: number;
  pageSize?: number;
}): Promise<CaseListResponse> => {
  return get<CaseListResponse>('/cases/my', { params });
};

/**
 * Get cases due soon
 */
export const getCasesDueSoon = async (days: number = 7): Promise<Case[]> => {
  return get<Case[]>('/cases/due-soon', {
    params: { days },
  });
};

/**
 * Clone case
 */
export const cloneCase = async (id: number, data: {
  title: string;
  clientId?: number;
}): Promise<Case> => {
  return post<Case>(`/cases/${id}/clone`, data);
};

/**
 * Update case parties
 */
export const updateCaseParties = async (
  caseId: number,
  parties: CaseParty[]
): Promise<{ msg: string }> => {
  return put<{ msg: string }>(`/cases/${caseId}/parties`, { parties });
};

/**
 * Update case detail
 */
export const updateCaseDetail = async (
  caseId: number,
  detail: Partial<CaseDetail>
): Promise<CaseDetail> => {
  return put<CaseDetail>(`/cases/${caseId}/detail`, detail);
};

/**
 * Get case collaborators
 */
export const getCaseCollaborators = async (caseId: number): Promise<any[]> => {
  return get<any[]>(`/cases/${caseId}/collaborators`);
};

/**
 * Invite collaborator
 */
export const inviteCollaborator = async (
  caseId: number,
  data: {
    collaborator_ids: number[];
    role: string;
    permissions: string[];
    notes?: string;
    send_notification: boolean;
  }
): Promise<any[]> => {
  return post<any[]>(`/cases/${caseId}/collaborators`, data);
};

/**
 * Remove collaborator
 */
export const removeCollaborator = async (
  caseId: number,
  userId: number
): Promise<{ msg: string }> => {
  return del<{ msg: string }>(`/cases/${caseId}/collaborators/${userId}`);
};

/**
 * Get case milestones
 */
export const getCaseMilestones = async (caseId: number): Promise<CaseMilestone[]> => {
  const caseData = await getCaseById(caseId);
  return caseData.milestones || [];
};

/**
 * Add case milestone
 */
export const addCaseMilestone = async (
  caseId: number,
  data: {
    milestone_type: string;
    description: string;
  }
): Promise<CaseMilestone> => {
  return post<CaseMilestone>(`/cases/${caseId}/milestones`, data);
};
/**

 * Batch update responsible lawyer for cases
 */
export const batchUpdateResponsibleLawyer = async (
  caseIds: number[],
  responsibleLawyerId: number
): Promise<{
  msg: string;
  success_count: number;
  failed_count: number;
  failed_cases: Array<{ id: number; reason: string }>;
}> => {
  return post<{
    msg: string;
    success_count: number;
    failed_count: number;
    failed_cases: Array<{ id: number; reason: string }>;
  }>('/cases/batch-update-responsible-lawyer', {
    case_ids: caseIds,
    responsible_lawyer_id: responsibleLawyerId,
  });
};
